import { useEffect, useRef, useState } from "react";
import {
  MCP_BACKEND_NOTIFICATION_EVENT,
  type BackendNotificationPayload,
  type BackendNotificationTarget
} from "./mcp-backend-notifications";

/**
 * Per-icon subscription to AI backend tool-activity notifications, used by any
 * icon that wants to light up (shimmer) and show a reveal tooltip when the AI
 * runs a tool that touches its surface.
 *
 * The top toolbar keeps its own central listener in `toolbar-left.tsx` (it also
 * drives side effects like opening the datasources panel); this hook is the
 * left-menu counterpart and the canonical way to add reveal feedback to a new
 * icon — pass the targets it cares about, render the returned shimmer flags +
 * `notifications` through `KuikaRevealTooltip`.
 */

/** Icons in the left menu that mirror a backend tool-activity surface. */
export type LeftMenuRevealIconKey = "datasources" | "language" | "scheduled-jobs" | "api-management";

/**
 * Which notification targets light up each left-menu icon.
 *
 * The datasources LEFT-MENU button (the Datasources panel = custom/SQL actions)
 * is action-specific: it mirrors ONLY `panel-datasources` (SQL/SAP actions
 * created, actions deleted). Table/schema/datasource-level events
 * (`toolbar-datasources` — table created, REST/C# datasource saved, …) belong
 * to the top-toolbar data-diagram icon and are intentionally NOT duplicated on
 * the left. Language has no toolbar icon, so it only mirrors the language panel.
 */
export const LEFT_MENU_REVEAL_TARGETS: Record<LeftMenuRevealIconKey, readonly BackendNotificationTarget[]> = {
  datasources: ["panel-datasources"],
  language: ["panel-language"],
  "scheduled-jobs": ["toolbar-scheduled-jobs"],
  "api-management": ["toolbar-api-management"]
};

/** The left-menu icons a given notification target should light up (pure, testable). */
export function findLeftMenuIconsForTarget(target: BackendNotificationTarget): LeftMenuRevealIconKey[] {
  const keys: LeftMenuRevealIconKey[] = Object.keys(LEFT_MENU_REVEAL_TARGETS) as LeftMenuRevealIconKey[];
  return keys.filter((key: LeftMenuRevealIconKey): boolean => LEFT_MENU_REVEAL_TARGETS[key].includes(target));
}

/** Dedup key collapsing the openOnly + deferred double-dispatch panel-* targets share (same timestamp). */
export function revealDedupeKey(payload: BackendNotificationPayload): string {
  return `${payload.timestamp}-${payload.target}-${payload.message}`;
}

export interface RevealNotificationsState {
  /** Append-only feed for `KuikaRevealTooltip`; reset to [] once the window goes idle. */
  notifications: BackendNotificationPayload[];
  shimmer: boolean;
  shimmerDelete: boolean;
}

/** Matches `toolbar-left.tsx`'s SHIMMER_DURATION_MS and the tooltip's DISMISS_MS. */
const SHIMMER_DURATION_MS = 8000;

export function useRevealNotifications(targets: readonly BackendNotificationTarget[]): RevealNotificationsState {
  const [notifications, setNotifications] = useState<BackendNotificationPayload[]>([]);
  const [shimmer, setShimmer] = useState<boolean>(false);
  const [shimmerDelete, setShimmerDelete] = useState<boolean>(false);

  const seenRef = useRef<Set<string>>(new Set<string>());
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read targets through a ref so the listener never needs to re-subscribe.
  const targetsRef = useRef<readonly BackendNotificationTarget[]>(targets);
  targetsRef.current = targets;

  useEffect(() => {
    const handleNotification = (event: Event): void => {
      const payload: BackendNotificationPayload = (event as CustomEvent<BackendNotificationPayload>).detail;
      if (!payload || !targetsRef.current.includes(payload.target)) return;

      const key: string = revealDedupeKey(payload);
      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);

      setNotifications((prev: BackendNotificationPayload[]): BackendNotificationPayload[] => [...prev, payload]);
      setShimmer(true);
      if (payload.isDelete) setShimmerDelete(true);

      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => {
        setShimmer(false);
        setShimmerDelete(false);
        setNotifications([]);
        seenRef.current.clear();
        clearTimerRef.current = null;
      }, SHIMMER_DURATION_MS);
    };

    window.addEventListener(MCP_BACKEND_NOTIFICATION_EVENT, handleNotification as EventListener);
    return () => {
      window.removeEventListener(MCP_BACKEND_NOTIFICATION_EVENT, handleNotification as EventListener);
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, []);

  return { notifications, shimmer, shimmerDelete };
}
